import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ImageUploader } from "@/components/ImageUploader";
import { VideoUploader } from "@/components/VideoUploader";
import { ListingImage } from "@/components/ListingImage";
import { US_STATES } from "@/lib/listings";
import { STORE_TYPES, PRODUCT_CATEGORIES, UNITS, lookup } from "@/lib/grocery";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Save, X, Store as StoreIcon, Package, Sparkles, Loader2 } from "lucide-react";

const STATUS_LABEL = (s, t) => ({
  pending: t("قيد المراجعة", "Pending"),
  approved: t("منشور", "Live"),
  rejected: t("مرفوض", "Rejected"),
  archived: t("مؤرشف", "Archived")
}[s] || s);

const STATUS_COLOR = {
  pending: "bg-yellow-500/20 text-yellow-400",
  approved: "bg-green-500/20 text-green-400",
  rejected: "bg-red-500/20 text-red-400",
  archived: "bg-slate-700 text-slate-300"
};

const blankStore = {
  name: "", name_ar: "", store_type: "halal_market", description: "",
  address: "", state: "", city: "", zip_code: "", phone: "", email: "",
  logo: null, photos: [], videos: [], website: "", catalog_provider: "", catalog_url: "", delivery_fee: 5.99
};

const blankProduct = {
  name: "", name_ar: "", category: "halal_meat", description: "",
  price: "", unit: "piece", images: [], in_stock: true
};

export default function MyStorePortal() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeForm, setStoreForm] = useState(blankStore);
  const [editStore, setEditStore] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(blankProduct);
  const [busy, setBusy] = useState(false);

  const loadStores = async () => {
    try {
      const res = await axios.get(`${API}/stores/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setStores(res.data);
      if (res.data.length > 0 && !active) setActive(res.data[0]);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadProducts = async (storeId) => {
    try {
      const res = await axios.get(`${API}/stores/${storeId}/products`);
      setProducts(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadStores(); }, []); // eslint-disable-line
  useEffect(() => { if (active) loadProducts(active.store_id); }, [active]);

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-yellow-500 outline-none";

  const saveStore = async () => {
    if (!storeForm.name || !storeForm.description || !storeForm.address || !storeForm.state || !storeForm.city || !storeForm.zip_code || !storeForm.phone) {
      toast.error(t("يرجى تعبئة الحقول المطلوبة", "Fill required fields")); return;
    }
    setBusy(true);
    try {
      const payload = { ...storeForm, delivery_fee: parseFloat(storeForm.delivery_fee) || 5.99 };
      if (editStore && active) {
        await axios.put(`${API}/stores/${active.store_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم التحديث", "Updated"));
      } else {
        const res = await axios.post(`${API}/stores`, payload, { headers: { Authorization: `Bearer ${token}` } });
        // Register every uploaded store photo with the moderation pipeline.
        const newId = res.data?.store_id;
        if (newId && (storeForm.photos || []).length > 0) {
          await Promise.all(storeForm.photos.map((path) =>
            axios.post(`${API}/media`, {
              listing_type: "store", listing_id: newId, url: path, media_type: "image",
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
          ));
        }
        if (newId && (storeForm.videos || []).length > 0) {
          await Promise.all(storeForm.videos.map((path) =>
            axios.post(`${API}/media`, {
              listing_type: "store", listing_id: newId, url: path, media_type: "video",
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
          ));
        }
        toast.success(t("تم إرسال المتجر للمراجعة", "Submitted for review"));
        setStoreForm(blankStore);
      }
      setEditStore(false);
      await loadStores();
    } catch (e) { toast.error(e.response?.data?.detail || t("فشلت العملية", "Failed")); }
    finally { setBusy(false); }
  };

  const startEdit = () => {
    setStoreForm({ ...active });
    setEditStore(true);
  };

  const saveProduct = async () => {
    if (!productForm.name || !productForm.price) { toast.error(t("الاسم والسعر مطلوبان", "Name and price required")); return; }
    setBusy(true);
    try {
      const payload = { ...productForm, price: parseFloat(productForm.price) };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم التحديث", "Updated"));
      } else {
        await axios.post(`${API}/stores/${active.store_id}/products`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تمت إضافة المنتج", "Product added"));
      }
      setShowProductForm(false); setEditingProduct(null); setProductForm(blankProduct);
      await loadProducts(active.store_id);
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل", "Failed")); }
    finally { setBusy(false); }
  };

  const editProduct = (p) => {
    setProductForm({
      name: p.name, name_ar: p.name_ar || "", category: p.category, description: p.description || "",
      price: p.price, unit: p.unit, images: p.images || [], in_stock: p.in_stock
    });
    setEditingProduct(p.product_id); setShowProductForm(true);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Delete?"))) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      await loadProducts(active.store_id);
    } catch (e) { toast.error(t("فشل", "Failed")); }
  };

  if (loading) return <DashboardLayout title=""><div className="glass-card rounded-2xl h-40 skeleton" /></DashboardLayout>;

  // No stores yet → registration form
  if (stores.length === 0) {
    return (
      <DashboardLayout title={t("سجّل متجرك", "Register Your Store")}>
        <div className="max-w-4xl space-y-5" data-testid="store-register-page">
          <div className="glass-card rounded-3xl p-8 bg-luxury relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full" />
            <div className="relative">
              <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-2">
                <StoreIcon size={12} className="ml-1" /> {t("لأصحاب المتاجر", "Store owners")}
              </Badge>
              <h2 className="text-2xl lg:text-3xl font-bold text-luxury">{t("ابدأ بيع منتجاتك للعرب في أمريكا", "Start selling to Arabs in America")}</h2>
              <p className="text-slate-400 text-sm mt-1">{t("سجّل متجرك، أضف منتجاتك، ودعنا نأتيك بالعملاء.", "Register your store, list products, let us bring you customers.")}</p>
            </div>
          </div>
          <StoreForm storeForm={storeForm} setStoreForm={setStoreForm} t={t} language={language} inputCls={inputCls} />
          <div className="flex justify-end">
            <Button onClick={saveStore} disabled={busy} className="btn-primary" data-testid="register-store-btn">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Save size={14} className="ml-2" />{t("إرسال للمراجعة", "Submit for review")}</>}
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("بوابة المتجر", "Store Owner Portal")}>
      <div className="space-y-5" data-testid="store-portal">
        {/* Store selector */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {stores.map((s) => (
            <button key={s.store_id} onClick={() => setActive(s)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${active?.store_id === s.store_id ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`}>
              {language === "ar" ? (s.name_ar || s.name) : s.name}
            </button>
          ))}
          <button onClick={() => { setStoreForm(blankStore); setActive(null); setEditStore(false); }} className="px-4 py-2 rounded-full bg-slate-900 border border-dashed border-slate-700 text-slate-400 text-sm whitespace-nowrap">
            <Plus size={14} className="inline ml-1" /> {t("متجر جديد", "New store")}
          </button>
        </div>

        {!active ? (
          <>
            <StoreForm storeForm={storeForm} setStoreForm={setStoreForm} t={t} language={language} inputCls={inputCls} />
            <div className="flex justify-end">
              <Button onClick={saveStore} disabled={busy} className="btn-primary"><Save size={14} className="ml-2" />{t("إنشاء متجر", "Create store")}</Button>
            </div>
          </>
        ) : (
          <>
            {/* Active store header */}
            <div className="glass-card rounded-2xl p-5 flex justify-between items-start gap-4 flex-wrap">
              <div className="flex gap-4">
                {active.logo && <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-900"><ListingImage path={active.logo} alt="" className="w-full h-full object-cover" /></div>}
                <div>
                  <Badge className={STATUS_COLOR[active.status]}>{STATUS_LABEL(active.status, t)}</Badge>
                  <h2 className="text-xl font-bold text-white mt-1">{language === "ar" ? (active.name_ar || active.name) : active.name}</h2>
                  <p className="text-sm text-slate-400">{lookup(STORE_TYPES, active.store_type, language)} · {active.city}, {active.state}</p>
                </div>
              </div>
              <Button onClick={startEdit} variant="outline" className="border-slate-700 text-white"><Edit size={14} className="ml-2" />{t("تعديل المتجر", "Edit store")}</Button>
            </div>

            {editStore && (
              <>
                <StoreForm storeForm={storeForm} setStoreForm={setStoreForm} t={t} language={language} inputCls={inputCls} />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setEditStore(false)} className="text-slate-300">{t("إلغاء", "Cancel")}</Button>
                  <Button onClick={saveStore} disabled={busy} className="btn-primary"><Save size={14} className="ml-2" />{t("حفظ", "Save")}</Button>
                </div>
              </>
            )}

            {/* Products */}
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-white flex items-center gap-2"><Package className="text-yellow-500" size={20} />{t("المنتجات", "Products")} ({products.length})</h3>
              {!showProductForm && (
                <Button onClick={() => { setEditingProduct(null); setProductForm(blankProduct); setShowProductForm(true); }} className="btn-primary" data-testid="new-product-btn">
                  <Plus size={14} className="ml-2" /> {t("منتج جديد", "New product")}
                </Button>
              )}
            </div>

            {showProductForm && (
              <div className="glass-card rounded-2xl p-5 space-y-3" data-testid="product-form">
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-white">{editingProduct ? t("تعديل منتج", "Edit product") : t("منتج جديد", "New product")}</h4>
                  <button onClick={() => { setShowProductForm(false); setEditingProduct(null); }} className="text-slate-400"><X size={20} /></button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input className={inputCls} placeholder={t("الاسم *", "Name *")} value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} data-testid="pf-name" />
                  <input className={inputCls} placeholder={t("الاسم بالعربية", "Name (AR)")} value={productForm.name_ar} onChange={(e) => setProductForm({...productForm, name_ar: e.target.value})} />
                  <select className={inputCls} value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} data-testid="pf-cat">
                    {PRODUCT_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{language === "ar" ? c.ar : c.en}</option>)}
                  </select>
                  <select className={inputCls} value={productForm.unit} onChange={(e) => setProductForm({...productForm, unit: e.target.value})}>
                    {UNITS.map((u) => <option key={u.v} value={u.v}>{language === "ar" ? u.ar : u.en}</option>)}
                  </select>
                  <input className={inputCls} type="number" step="0.01" placeholder={t("السعر *", "Price *")} value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} data-testid="pf-price" />
                  <label className="flex items-center gap-2 text-white text-sm">
                    <input type="checkbox" checked={productForm.in_stock} onChange={(e) => setProductForm({...productForm, in_stock: e.target.checked})} />
                    {t("متوفر", "In stock")}
                  </label>
                </div>
                <textarea className={inputCls} rows={2} placeholder={t("الوصف", "Description")} value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} />
                <ImageUploader value={productForm.images} onChange={(imgs) => setProductForm({...productForm, images: imgs})} max={4} />
                <div className="flex gap-2 justify-end">
                  <Button onClick={saveProduct} disabled={busy} className="btn-primary" data-testid="save-product-btn">
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="ml-2" />{t("حفظ", "Save")}</>}
                  </Button>
                </div>
              </div>
            )}

            {products.length === 0 ? (
              <div className="glass-card rounded-2xl p-8 text-center text-slate-400">{t("لم تُضف منتجات بعد", "No products yet")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {products.map((p) => (
                  <div key={p.product_id} className="glass-card rounded-2xl p-3 flex gap-3" data-testid={`mp-product-${p.product_id}`}>
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-900 flex-shrink-0">
                      <ListingImage path={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white truncate text-sm">{p.name}</h4>
                      <p className="text-xs text-slate-500">{lookup(PRODUCT_CATEGORIES, p.category, language)}</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-yellow-400 text-sm font-bold">${p.price}/{p.unit}</span>
                        <div className="flex gap-1">
                          <button onClick={() => editProduct(p)} className="text-slate-400 hover:text-yellow-400"><Edit size={14} /></button>
                          <button onClick={() => deleteProduct(p.product_id)} className="text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

const StoreForm = ({ storeForm, setStoreForm, t, language, inputCls }) => (
  <div className="space-y-4">
    <div className="glass-card rounded-2xl p-5 space-y-3">
      <h3 className="font-bold text-white">{t("معلومات المتجر", "Store info")}</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <input className={inputCls} placeholder={t("اسم المتجر *", "Store name *")} value={storeForm.name} onChange={(e) => setStoreForm({...storeForm, name: e.target.value})} data-testid="sf-name" />
        <input className={inputCls} placeholder={t("الاسم بالعربية", "Name (AR)")} value={storeForm.name_ar} onChange={(e) => setStoreForm({...storeForm, name_ar: e.target.value})} />
        <select className={inputCls} value={storeForm.store_type} onChange={(e) => setStoreForm({...storeForm, store_type: e.target.value})} data-testid="sf-type">
          {STORE_TYPES.map((s) => <option key={s.v} value={s.v}>{language === "ar" ? s.ar : s.en}</option>)}
        </select>
        <input className={inputCls} placeholder={t("الهاتف *", "Phone *")} value={storeForm.phone} onChange={(e) => setStoreForm({...storeForm, phone: e.target.value})} data-testid="sf-phone" />
        <input className={inputCls} placeholder={t("البريد الإلكتروني", "Email")} value={storeForm.email} onChange={(e) => setStoreForm({...storeForm, email: e.target.value})} />
        <input className={inputCls} type="number" step="0.5" placeholder={t("رسوم التوصيل ($)", "Delivery fee ($)")} value={storeForm.delivery_fee} onChange={(e) => setStoreForm({...storeForm, delivery_fee: e.target.value})} />
      </div>
      <textarea className={inputCls} rows={2} placeholder={t("وصف المتجر *", "Description *")} value={storeForm.description} onChange={(e) => setStoreForm({...storeForm, description: e.target.value})} data-testid="sf-desc" />
    </div>

    <div className="glass-card rounded-2xl p-5 space-y-3">
      <h3 className="font-bold text-white">{t("الموقع", "Location")}</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <input className={inputCls} placeholder={t("العنوان *", "Address *")} value={storeForm.address} onChange={(e) => setStoreForm({...storeForm, address: e.target.value})} data-testid="sf-address" />
        <input className={inputCls} placeholder={t("المدينة *", "City *")} value={storeForm.city} onChange={(e) => setStoreForm({...storeForm, city: e.target.value})} data-testid="sf-city" />
        <select className={inputCls} value={storeForm.state} onChange={(e) => setStoreForm({...storeForm, state: e.target.value})} data-testid="sf-state">
          <option value="">{t("الولاية *", "State *")}</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
        </select>
        <input className={inputCls} placeholder={t("ZIP *", "ZIP *")} value={storeForm.zip_code} onChange={(e) => setStoreForm({...storeForm, zip_code: e.target.value})} data-testid="sf-zip" />
      </div>
    </div>

    <div className="glass-card rounded-2xl p-5 space-y-3">
      <h3 className="font-bold text-white">{t("الصور (شعار + صور المتجر)", "Images (logo + photos)")}</h3>
      <label className="text-xs text-slate-400 block">{t("الشعار", "Logo")}</label>
      <ImageUploader value={storeForm.logo ? [storeForm.logo] : []} onChange={(imgs) => setStoreForm({...storeForm, logo: imgs[0] || null})} max={1} />
      <label className="text-xs text-slate-400 block">{t("صور إضافية", "Additional photos")}</label>
      <ImageUploader
        value={storeForm.photos}
        onChange={(imgs) => setStoreForm({...storeForm, photos: imgs})}
        listingType={editStore && active ? "store" : undefined}
        listingId={editStore && active ? active.store_id : undefined}
        mediaType="image"
      />
    </div>

    {/* Videos — unlimited, admin-reviewed before going live */}
    <div className="glass-card rounded-2xl p-5 space-y-3">
      <VideoUploader
        value={storeForm.videos || []}
        onChange={(vids) => setStoreForm({...storeForm, videos: vids})}
        listingType={editStore && active ? "store" : undefined}
        listingId={editStore && active ? active.store_id : undefined}
      />
    </div>

    <div className="glass-card rounded-2xl p-5 space-y-3">
      <h3 className="font-bold text-white flex items-center gap-2"><Sparkles size={16} className="text-yellow-500" />{t("تكامل الكتالوج (اختياري)", "Catalog integration (optional)")}</h3>
      <p className="text-xs text-slate-500">{t("اربط متجرك بـ Shopify أو Square أو WooCommerce لمزامنة المنتجات تلقائياً (قريباً).", "Connect Shopify, Square or WooCommerce for auto-sync (coming soon).")}</p>
      <div className="grid md:grid-cols-2 gap-3">
        <select className={inputCls} value={storeForm.catalog_provider || ""} onChange={(e) => setStoreForm({...storeForm, catalog_provider: e.target.value || null})}>
          <option value="">{t("بدون تكامل", "No integration")}</option>
          <option value="shopify">Shopify</option>
          <option value="square">Square</option>
          <option value="woocommerce">WooCommerce</option>
          <option value="custom">Custom</option>
        </select>
        <input className={inputCls} placeholder={t("رابط الكتالوج", "Catalog URL")} value={storeForm.catalog_url || ""} onChange={(e) => setStoreForm({...storeForm, catalog_url: e.target.value})} />
      </div>
    </div>
  </div>
);
